<script>
import '@vuepic/vue-datepicker/dist/main.css';
import SettingsModalBase from '@/components/modals/SettingsModalBase';
import keycloakService from '@/services/KeycloakService';
import { jwtDecode } from 'jwt-decode';
import { useI18n } from 'vue-i18n';

export default {
    props: [
        'settingsShow',
    ],
    emits: [
        'close',
        'toggleMenu',
    ],
    components: {
        SettingsModalBase,
    },
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    data() {
        return {
            decodedJwt: {},
            languages: [
                { code: 'ru', label: 'Русский' },
                { code: 'en', label: 'English' },
            ],
        };
    },
    methods: {
        logout() {
            keycloakService().doLogout();
        },
        getUsername() {
            this.decodedJwt = jwtDecode(keycloakService().getToken());
        },
        changeLocale(code) {
            this.locale = code;
            localStorage.setItem('locale', code);
        },
    },
    mounted() {
        this.getUsername();
    }
};
</script>

<template>
    <SettingsModalBase
        :show="settingsShow"
        @close="$emit('close')"
        :closeable="true"
        max-width="xsm">

        <div class="flex flex-col justify-between gap-2 min-h-[500px]">
            <div class="flex w-full flex-col pt-2 gap-5 text-gray-400">
                <div class="flex w-full justify-center flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                    <p class="text-sm font-bold">{{ $t('Настройки') }}</p>
                </div>

                <div class="flex w-full flex-col gap-1">
                    <div class="flex w-full justify-between items-center border-b-[1px] border-gray-400 pb-2">
                        <p class="text-sm">{{ $t('Язык') }}</p>
                        <select
                            :value="locale"
                            @change="changeLocale($event.target.value)"
                            class="text-sm bg-transparent border border-gray-300 rounded-md px-2 py-1 text-gray-400 focus:outline-none cursor-pointer">
                            <option
                                v-for="lang in languages"
                                :key="lang.code"
                                :value="lang.code">
                                {{ lang.label }}
                            </option>
                        </select>
                    </div>
                    <div id="settings"/>
                </div>
            </div>

            <div class="flex w-full justify-between items-center gap-4 text-gray-400">
                <button
                    type="button"
                    @click="logout()"
                    class="flex justify-start gap-4 items-center hover:bg-red-500 hover:text-white px-4 py-2 rounded-md transition-all">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class=" w-6 h-6">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
                    </svg>
                </button>
                <p class=" font-bold flex gap-2 items-center whitespace-nowrap">
                    {{ decodedJwt.name }}
                </p>
                <div class="flex px-4 py-2">
                    <div class="relative group">
                        <svg
                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 hover:cursor-pointer">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                        </svg>
                        <div class="hidden absolute bottom-10 right-0 group-hover:flex bg-white text-black flex-col rounded-md p-2">
                            <div class="flex flex-col gap-1" v-if="decodedJwt.realm_access">
                                <p class="text-[12px] whitespace-nowrap" :key="role" v-for="role in decodedJwt.realm_access.roles" v-html="role"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </SettingsModalBase>
</template>